import useLatestRequest from '../hooks/useLatestRequest';
import RefreshRegion from '../components/common/RefreshRegion';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  SpeakerWaveIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import AnnouncementService from '../services/announcement.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const Announcements = () => {
  const { user, isAdmin, isHR } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [announcements, setAnnouncements] = useState([]);
  const { loading, begin: beginFetchAnnouncements } = useLatestRequest();
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    priority: 'normal',
    targetAudience: 'all',
    expiryDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [filter]);

  // Listen for new announcements via WebSocket
  useEffect(() => {
    const { socket } = require('../utils/socket');
    
    const handleNewAnnouncement = (announcement) => {
      console.log('📢 New announcement received:', announcement);
      setAnnouncements(prev => [announcement, ...prev]);
      toast.success(`New announcement: ${announcement.title}`);
    };

    socket.on('announcement:new', handleNewAnnouncement);

    return () => {
      socket.off('announcement:new', handleNewAnnouncement);
    };
  }, []);

  const fetchAnnouncements = async () => {
    const request = beginFetchAnnouncements();
    try {
      const response = await AnnouncementService.getAnnouncements({ filter });
      if (!request.isCurrent()) return;
      
      if (response && response.success) {
        const fetchedAnnouncements = response.data || [];
        setAnnouncements(fetchedAnnouncements);
      } else {
        console.warn('API response not in expected format:', response);
        setAnnouncements([]);
      }
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
    } finally {
      request.finish();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        type: formData.type,
        priority: formData.priority
      };

      if (editingAnnouncement) {
        const response = await AnnouncementService.updateAnnouncement(editingAnnouncement.id, submitData);
        if (response && response.success) {
          toast.success('Announcement updated successfully');
        }
      } else {
        const response = await AnnouncementService.createAnnouncement(submitData);
        if (response && response.success) {
          toast.success('Announcement created successfully');
        }
      }
      
      setShowForm(false);
      setEditingAnnouncement(null);
      setFormData({
        title: '',
        content: '',
        type: 'info',
        priority: 'normal',
        targetAudience: 'all',
        expiryDate: '',
        isActive: true
      });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    }
  };

  const handleDelete = async (announcementId) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        const response = await AnnouncementService.deleteAnnouncement(announcementId);
        if (response && response.success) {
          toast.success('Announcement deleted successfully');
          fetchAnnouncements();
        }
      } catch (error) {
        console.error('Error deleting announcement:', error);
        toast.error('Failed to delete announcement');
      }
    }
  };

  const getAnnouncementIcon = (type) => {
    switch (type) {
      case 'success': return CheckCircleIcon;
      case 'warning': return ExclamationTriangleIcon;
      case 'error': return ExclamationTriangleIcon;
      default: return InformationCircleIcon;
    }
  };

  const getAnnouncementColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600 bg-green-100 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-blue-600 bg-blue-100 border-blue-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return `${formatDateDisplay(date)} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const filterOptions = [
    { value: 'all', label: 'All Announcements' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'high', label: 'High Priority' }
  ];

  const filteredAnnouncements = announcements.filter(announcement => {
    if (filter !== 'all') {
      if (filter === 'active' && !announcement.isActive) return false;
      if (filter === 'expired' && announcement.isActive && new Date(announcement.expiryDate) >= new Date()) return false;
      if (filter === 'high' && announcement.priority !== 'high') return false;
    }
    if (headerSearch) {
      const q = headerSearch.toLowerCase();
      return (
        (announcement.title && announcement.title.toLowerCase().includes(q)) ||
        (announcement.content && announcement.content.toLowerCase().includes(q)) ||
        (announcement.author && announcement.author.toLowerCase().includes(q)) ||
        (announcement.type && announcement.type.toLowerCase().includes(q))
      );
    }
    return true;
  });


  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-xl p-6 text-white"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Company Announcements</h1>
            <p className="text-amber-100">Stay informed with the latest company news and updates</p>
          </div>
          {(isAdmin || isHR) && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-white bg-opacity-20 text-white border-2 border-white hover:bg-white hover:text-orange-600 transition-all duration-200"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              New Announcement
            </Button>
          )}
        </div>
      </motion.div>

      {/* Filter Tabs */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-wrap gap-4">
            {filterOptions.map((option) => (
              <Button
                key={option.value}
                variant={filter === option.value ? 'primary' : 'outline'}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Announcements List */}
      <RefreshRegion loading={loading}>
      <div className="space-y-6">
        {filteredAnnouncements.length > 0 ? (
          filteredAnnouncements.map((announcement, index) => {
            const Icon = getAnnouncementIcon(announcement.type);
            const isExpired = new Date(announcement.expiryDate) < new Date();
            
            return (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Card className={`hover:shadow-lg transition-shadow duration-300 ${
                  announcement.priority === 'high' ? 'ring-2 ring-orange-200' : ''
                } ${isExpired ? 'opacity-75' : ''}`}>
                  <Card.Body className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className={`p-3 rounded-full border-2 ${getAnnouncementColor(announcement.type)}`}>
                        <Icon className="w-6 h-6" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h2 className="text-xl font-bold text-gray-900">{announcement.title}</h2>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(announcement.priority)}`}>
                                {announcement.priority.toUpperCase()}
                              </span>
                              {!announcement.isActive && (
                                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                  INACTIVE
                                </span>
                              )}
                              {isExpired && (
                                <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                  EXPIRED
                                </span>
                              )}
                            </div>
                            
                            <p className="text-gray-700 leading-relaxed mb-4">{announcement.content}</p>
                            
                            <div className="flex items-center space-x-6 text-sm text-gray-500">
                              <div className="flex items-center space-x-1">
                                <UserIcon className="w-4 h-4" />
                                <span>{announcement.author}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <CalendarIcon className="w-4 h-4" />
                                <span>{formatDate(announcement.createdAt)}</span>
                              </div>
                              {announcement.expiryDate && (
                                <div className="flex items-center space-x-1">
                                  <span>Expires: {formatDate(announcement.expiryDate)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          {(isAdmin || isHR) && (
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => {
                                  setEditingAnnouncement(announcement);
                                  setFormData({
                                    title: announcement.title,
                                    content: announcement.content,
                                    type: announcement.type,
                                    priority: announcement.priority,
                                    targetAudience: announcement.targetAudience,
                                    expiryDate: announcement.expiryDate ? announcement.expiryDate.split('T')[0] : '',
                                    isActive: announcement.isActive
                                  });
                                  setShowForm(true);
                                }}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => handleDelete(announcement.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            );
          })
        ) : (
          <Card>
            <Card.Body className="p-12 text-center">
              <SpeakerWaveIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Announcements</h3>
              <p className="text-gray-600 mb-4">
                {filter === 'all' 
                  ? "No announcements have been posted yet."
                  : `No ${filter} announcements found.`
                }
              </p>
              {(isAdmin || isHR) && (
                <Button onClick={() => setShowForm(true)}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create First Announcement
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
      </RefreshRegion>

      {/* Announcement Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-bold mb-4">
              {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter announcement title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows="6"
                  placeholder="Enter announcement content..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="info">Information</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Important</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="all">All Employees</option>
                    <option value="management">Management Only</option>
                    <option value="hr">HR Department</option>
                    <option value="employee">Employees</option>
                    <option value="manager">Managers</option>
                    <option value="admin">Administrators</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      min={editingAnnouncement ? undefined : new Date().toISOString().split('T')[0]}
                    />
                    {editingAnnouncement && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, expiryDate: '' }))}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
                        title="Clear expiry date"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {editingAnnouncement && (
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty for no expiry date, or set a new date
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Publish immediately (active)
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1">
                  {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAnnouncement(null);
                    setFormData({
                      title: '',
                      content: '',
                      type: 'info',
                      priority: 'normal',
                      targetAudience: 'all',
                      expiryDate: '',
                      isActive: true
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Announcements;
